import { PROCESS_STEPS, BUSINESS } from "../../../mocks/businessData";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const PROCESS_IMG =
  "https://public.readdy.ai/ai/img_res/eb900b151f7255fb41cec412a5ccd4a5.jpg";

export default function ProcessSection() {
  const ref = useScrollReveal();

  return (
    <section className="bg-stone-50 overflow-hidden" ref={ref}>
      {/* ── Header ── */}
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <div className="reveal text-center">
          <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-4">
            The White-Glove Experience
          </p>
          <h2 className="font-serif font-light text-4xl md:text-6xl text-stone-900 mb-5 leading-[1.05]">
            Effortless for You.
            <span className="block italic text-amber-700">Exceptional Every Time.</span>
          </h2>
          <div className="gold-rule mx-auto mb-6" />
          <p className="text-stone-500 text-base max-w-xl mx-auto leading-relaxed">
            We handle everything from first call to final reinstall — so you can simply enjoy
            the transformation.
          </p>
        </div>
      </div>

      {/* ── Steps + Image ── */}
      <div className="flex flex-col lg:flex-row">
        {/* Left: Steps */}
        <div className="lg:w-1/2 bg-white px-8 md:px-14 lg:px-20 py-16">
          <div className="max-w-lg ml-auto">
            {PROCESS_STEPS.map((step, i) => (
              <div
                key={step.step}
                className={`reveal reveal-delay-${i + 1} flex gap-7 ${
                  i < PROCESS_STEPS.length - 1 ? "mb-10 pb-10 border-b border-stone-100" : ""
                }`}
              >
                {/* Step number */}
                <div className="flex-shrink-0 flex flex-col items-center">
                  <div className="w-12 h-12 rounded-sm bg-amber-700 flex items-center justify-center shadow-sm">
                    <span className="font-serif text-white text-base font-medium">{step.step}</span>
                  </div>
                  {i < PROCESS_STEPS.length - 1 && (
                    <div className="flex-1 w-px bg-amber-200 mt-3 min-h-[2.5rem]" />
                  )}
                </div>

                {/* Content */}
                <div className="pt-2.5">
                  <h3 className="font-serif text-2xl text-stone-900 mb-2 leading-tight">{step.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}

            {/* CTA */}
            <div className="reveal reveal-delay-5 mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:19734891019"
                className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm whitespace-nowrap cursor-pointer text-center"
              >
                <i className="ri-phone-fill mr-2" />
                {BUSINESS.phone}
              </a>
              <a
                href="#quote-form"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-luxury bg-stone-900 hover:bg-stone-800 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm whitespace-nowrap cursor-pointer text-center"
              >
                Schedule Pickup
              </a>
            </div>
          </div>
        </div>

        {/* Right: Cinematic Image */}
        <div className="lg:w-1/2 relative overflow-hidden min-h-[400px] lg:min-h-[620px]">
          <img
            src={PROCESS_IMG}
            alt="Expert drapery cleaning process at The Drapery & Blind Co. professional facility"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-white/10" />

          {/* Floating guarantee badge */}
          <div className="absolute top-8 right-8">
            <div className="glass-card rounded-sm px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-700/30 rounded-sm flex-shrink-0">
                  <i className="ri-shield-check-fill text-amber-400 text-lg" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Fully Insured</p>
                  <p className="text-white/55 text-xs">Liability &amp; Workers&apos; Comp</p>
                </div>
              </div>
            </div>
          </div>

          {/* Floating availability badge */}
          <div className="absolute bottom-8 left-8">
            <div className="glass-card rounded-sm px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-700/30 rounded-sm flex-shrink-0">
                  <i className="ri-time-fill text-amber-400 text-lg" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Open 24 / 7</p>
                  <p className="text-white/55 text-xs">Emergency service available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
